import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import { API, useAuth } from "../AuthContext";
import { useI18n } from "../i18n";
import { Header, eur, CAT_COLORS, LockBadge, Footer } from "../components/Frumy";
import { RatingCard, FeedbackForm } from "../components/Feedback";
import {
  ArrowsClockwise, ShoppingBag, Trash, TrendUp, TrendDown, CalendarCheck,
  Lightning, Crown, ClockCounterClockwise, FilePdf, FileCsv, Plus, Lock,
  Bank, Stack, Warning, Question, X,
} from "@phosphor-icons/react";

const FREE_SUB = 8, FREE_MERCH = 5;

function daysUntil(iso) {
  if (!iso) return null;
  const d = Math.ceil((new Date(iso) - new Date()) / 86400000);
  return d;
}

function ProGate({ isPro, label, children }) {
  const nav = useNavigate();
  const { t } = useI18n();
  if (isPro) return children;
  return (
    <div className="locked-overlay relative frumy-card overflow-hidden" data-testid="pro-gate">
      <div className="locked-content">{children}</div>
      <button onClick={() => nav("/pro")}
        className="absolute inset-0 grid place-items-center bg-white/40 hover:bg-white/30 transition-colors">
        <span className="flex flex-col items-center gap-2">
          <span className="grid place-items-center h-11 w-11 rounded-full bg-[var(--frumy-lock)] text-white shadow">
            <Lock weight="fill" size={20} aria-hidden />
          </span>
          <LockBadge label={label} />
        </span>
      </button>
    </div>
  );
}

function AnnualAdvice({ advice, testid, onReport }) {
  const { t } = useI18n();
  if (!advice) return null;
  if (advice.type === "real_saving")
    return (
      <p data-testid={testid} data-advice="real_saving" className="mt-2 text-xs text-[var(--frumy-green)] bg-emerald-50 rounded-md px-2.5 py-1.5 leading-snug">
        💡 {t("adviceRealSaving")}: <b>{eur(advice.annual_saving)}/{t("year")}</b>
      </p>
    );
  if (advice.type === "no_annual_plan")
    return <p data-testid={testid} data-advice="no_annual_plan" className="mt-2 text-xs text-[var(--heading)] bg-[var(--frumy-muted)] rounded-md px-2.5 py-1.5 leading-snug">{t("adviceNoAnnual")}</p>;
  if (advice.type === "price_mismatch")
    return <p data-testid={testid} data-advice="price_mismatch" className="mt-2 text-xs text-[var(--frumy-lock)] bg-amber-50 rounded-md px-2.5 py-1.5 leading-snug">{t("advicePriceMismatch")}</p>;
  return (
    <div data-testid={testid} data-advice="unknown" className="mt-2">
      <p className="text-xs text-[var(--heading)] bg-[var(--frumy-muted)] rounded-md px-2.5 py-1.5 leading-snug">{t("adviceUnknown")}</p>
      <button data-testid={`${testid}-report`} onClick={onReport}
        className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-[var(--frumy-green)] hover:underline">
        <Question size={13} aria-hidden />{t("helpIdentify")}
      </button>
    </div>
  );
}

function ReportDialog({ sub, onClose }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ service_name: "", monthly_price: "", has_annual: false, annual_price: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/catalog-suggestions`, {
        service_name: (form.service_name || sub.custom_name || "").trim(),
        monthly_price: form.monthly_price ? parseFloat(form.monthly_price) : sub.amount,
        has_annual: form.has_annual,
        annual_price: form.has_annual && form.annual_price ? parseFloat(form.annual_price) : null,
        detected_name: sub.custom_name,
        detected_amount: sub.amount,
      });
      toast.success(t("thanksSuggestion"));
      onClose();
    } catch { toast.error(t("errGeneric")); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="report-dialog"
        className="frumy-card w-full max-w-md p-6 bg-white">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-xl font-bold">{t("helpIdentify")}</h3>
          <button type="button" data-testid="report-close" onClick={onClose} className="grid place-items-center h-8 w-8 rounded-full hover:bg-[var(--frumy-muted)]"><X size={16} aria-hidden /></button>
        </div>
        <p className="text-xs text-[var(--text-muted)] mt-1">{t("detected")}: <b>{sub.custom_name}</b> · {eur(sub.amount)}</p>
        <div className="space-y-3 mt-4">
          <div>
            <label className="text-xs tracking-[0.12em] uppercase font-bold text-[var(--text-muted)]">{t("reportServiceName")}</label>
            <input data-testid="report-service-name" value={form.service_name} onChange={(e) => setForm({ ...form, service_name: e.target.value })}
              placeholder={sub.custom_name} className="mt-1 w-full rounded-lg border border-[var(--frumy-border)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--frumy-green)]" />
          </div>
          <div>
            <label className="text-xs tracking-[0.12em] uppercase font-bold text-[var(--text-muted)]">{t("reportMonthly")}</label>
            <input data-testid="report-monthly" type="number" step="0.01" value={form.monthly_price} onChange={(e) => setForm({ ...form, monthly_price: e.target.value })}
              className="mt-1 w-full rounded-lg border border-[var(--frumy-border)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--frumy-green)]" />
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-[var(--heading)]">
            <input data-testid="report-has-annual" type="checkbox" checked={form.has_annual} onChange={(e) => setForm({ ...form, has_annual: e.target.checked })} className="h-4 w-4 accent-[var(--frumy-green)]" />
            {t("reportHasAnnual")}
          </label>
          {form.has_annual && (
            <div>
              <label className="text-xs tracking-[0.12em] uppercase font-bold text-[var(--text-muted)]">{t("reportAnnual")}</label>
              <input data-testid="report-annual" type="number" step="0.01" value={form.annual_price} onChange={(e) => setForm({ ...form, annual_price: e.target.value })}
                className="mt-1 w-full rounded-lg border border-[var(--frumy-border)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--frumy-green)]" />
            </div>
          )}
        </div>
        <button data-testid="report-submit" type="submit" disabled={saving}
          className="pill-btn cta-metal w-full mt-5 text-white font-bold py-2.5 disabled:opacity-60">
          {saving ? "..." : t("reportSubmit")}
        </button>
      </form>
    </div>
  );
}


export default function Dashboard() {
  const { user, refresh } = useAuth();
  const { t, catLabel } = useI18n();
  const nav = useNavigate();
  const [scan, setScan] = useState(null);            // Free: latest scan
  const [accounts, setAccounts] = useState([]);       // Pro: one scan per account
  const [combined, setCombined] = useState(null);     // Pro: aggregated view
  const [selected, setSelected] = useState("combined"); // "combined" | account id
  const [loading, setLoading] = useState(true);
  const [reportFor, setReportFor] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const isPro = !!user?.is_pro;

  const load = async () => {
    try {
      if (isPro) {
        const [list, comb] = await Promise.all([
          axios.get(`${API}/scans`),
          axios.get(`${API}/scans/combined`),
        ]);
        const accs = list.data || [];
        setAccounts(accs);
        setCombined(comb.data);
        setScan(accs[0] || null);
        setSelected(accs.length > 1 ? "combined" : (accs[0]?.id || "combined"));
      } else {
        const latest = await axios.get(`${API}/scans/latest`);
        setScan(latest.data);
      }
    } catch (e) {
      // 401 handled globally by interceptor (redirects to login)
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const deleteAll = async () => {
    if (!window.confirm(t("deleteConfirm"))) return;
    await axios.delete(`${API}/data`);
    setScan(null); setAccounts([]); setCombined(null);
    toast.success(t("dataDeleted"));
  };

  const deleteAccount = async (a) => {
    if (!window.confirm(t("deleteAccountConfirm", { name: a.account_name }))) return;
    try {
      await axios.delete(`${API}/scans/${a.id}`);
      setSelected("combined");
      await load();
      toast.success(t("accountDeleted"));
    } catch (e) { toast.error(t("errGeneric")); }
  };

  if (loading) return <div className="min-h-screen"><Header /></div>;

  if (!scan) {
    return (
      <div className="min-h-screen"><Header />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <p className="text-[var(--text-muted)] mb-6">{t("noData")}</p>
          <button data-testid="empty-upload-btn" onClick={() => nav("/upload")}
            className="pill-btn cta-metal inline-flex items-center gap-2 text-white font-bold px-6 py-3">
            <Plus weight="bold" size={18} aria-hidden />{t("upload")}
          </button>
        </div>
      </div>
    );
  }

  const isCombined = isPro && selected === "combined";
  const view = isCombined
    ? (combined || scan)
    : (isPro ? (accounts.find((a) => a.id === selected) || accounts[0] || scan) : scan);

  const subs = view.subscriptions || [];
  const habs = view.habituals || [];
  const summary = view.summary || { monthly_total: 0, annual_total: 0, by_category: {} };
  const pieData = Object.entries(summary.by_category).map(([k, v]) => ({ name: catLabel(k), key: k, value: v }));

  const visibleSubs = isPro ? subs : subs.slice(0, FREE_SUB);
  const hiddenSubs = isPro ? 0 : Math.max(0, subs.length - FREE_SUB);
  const visibleHabs = isPro ? habs : habs.slice(0, FREE_MERCH);
  const hiddenHabs = isPro ? 0 : Math.max(0, habs.length - FREE_MERCH);

  const exportCsv = () => {
    const rows = [["Tipo", "Nome", "Categoria", "Importo", "Frequenza/Tx"]];
    subs.forEach((s) => rows.push(["Abbonamento", s.custom_name, s.category, s.amount, s.frequency]));
    habs.forEach((h) => rows.push(["Spesa abituale", h.custom_name, h.category, h.total_all != null ? h.total_all : h.total_current_month, `${h.tx_count} tx`]));
    const csv = rows.map((r) => r.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "frumy_report.csv"; a.click();
  };

  const exportPdf = async () => {
    const { default: jsPDF } = await import("jspdf");
    const autoTable = (await import("jspdf-autotable")).default;
    const doc = new jsPDF();
    doc.setFontSize(20); doc.setTextColor(5, 150, 105);
    doc.text("Frumy — Report", 14, 20);
    doc.setFontSize(11); doc.setTextColor(60);
    doc.text(`${t("monthlyTotal")}: ${eur(summary.monthly_total)}   ${t("annualTotal")}: ${eur(summary.annual_total)}`, 14, 30);
    autoTable(doc, {
      startY: 38, headStyles: { fillColor: [5, 150, 105] },
      head: [[t("subscriptions"), catLabel("altro"), t("frequency"), t("total")]],
      body: subs.map((s) => [s.custom_name, catLabel(s.category), s.frequency === "monthly" ? t("monthly") : t("annual"), eur(s.amount)]),
    });
    autoTable(doc, {
      startY: (doc.lastAutoTable?.finalY || 40) + 8, headStyles: { fillColor: [15, 23, 42] },
      head: [[t("habituals"), catLabel("altro"), t("transactions"), t("totalPeriod")]],
      body: habs.map((h) => [h.custom_name, catLabel(h.category), h.tx_count, eur(h.total_all != null ? h.total_all : h.total_current_month)]),
    });
    doc.save("frumy_report.pdf");
  };

  const trendData = [...accounts].reverse().map((s, i) => ({
    name: s.account_name || (s.period_end ? s.period_end.slice(5) : `#${i + 1}`),
    total: s.summary?.monthly_total || 0,
  }));

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Account selector (Pro multi-account) */}
        {isPro && accounts.length > 0 && (
          <div data-testid="account-selector" className="flex flex-wrap items-center gap-2 mb-6">
            <span className="flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase font-bold text-[var(--text-muted)] mr-1">
              <Bank size={16} aria-hidden />{t("account")}
            </span>
            {accounts.length > 1 && (
              <button data-testid="account-combined" onClick={() => setSelected("combined")}
                className={`pill-btn flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-bold ${isCombined ? "bg-[var(--frumy-navy)] text-white" : "border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)]"}`}>
                <Stack size={15} aria-hidden />{t("combinedView")}
              </button>
            )}
            {accounts.map((a) => {
              const active = !isCombined && selected === a.id;
              return (
                <div key={a.id} className={`pill-btn flex items-center overflow-hidden ${active ? "bg-[var(--frumy-green)] text-white" : "border border-[var(--frumy-border)]"}`}>
                  <button data-testid={`account-tab-${a.id}`} onClick={() => setSelected(a.id)}
                    className={`pl-3.5 pr-2 py-1.5 text-sm font-bold ${active ? "" : "hover:bg-[var(--frumy-muted)]"}`}>
                    {a.account_name}
                  </button>
                  <button data-testid={`account-delete-${a.id}`} onClick={() => deleteAccount(a)}
                    title={t("deleteAll")}
                    className={`grid place-items-center h-7 w-7 mr-1 rounded-full transition-colors ${active ? "hover:bg-white/20 text-white/80" : "text-[var(--text-muted)] hover:text-red-500 hover:bg-red-50"}`}>
                    <Trash size={13} aria-hidden />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Summary header */}
        <div className="grid md:grid-cols-3 gap-5">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="md:col-span-1 relative overflow-hidden rounded-xl p-6 bg-[var(--frumy-navy)] text-white grain">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-emerald-300">{t("monthlyTotal")}</p>
            <p data-testid="monthly-total" className="font-display text-4xl font-extrabold mt-2">{eur(summary.monthly_total)}</p>
            <p className="text-sm text-slate-300 mt-3">{t("annualTotal")}: <span data-testid="annual-total" className="font-bold text-white">{eur(summary.annual_total)}</span></p>
            <span className="absolute -right-6 -bottom-6 opacity-10"><Lightning weight="fill" size={120} aria-hidden /></span>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .05 }}
            className="md:col-span-2 frumy-card p-6">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[var(--text-muted)] mb-2">{t("byCategory")}</p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="shrink-0" style={{ width: 160, height: 160 }} data-testid="category-pie">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={72} paddingAngle={2}>
                      {pieData.map((e) => <Cell key={e.key} fill={CAT_COLORS[e.key] || "#94A3B8"} />)}
                    </Pie>
                    <Tooltip formatter={(v) => eur(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-full flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {pieData.map((e) => (
                  <div key={e.key} className="flex items-center gap-2 text-sm min-w-0">
                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: CAT_COLORS[e.key] || "#94A3B8" }} />
                    <span className="text-[var(--text-muted)] truncate">{e.name}</span>
                    <span className="ml-auto font-bold text-[var(--heading)] whitespace-nowrap">{eur(e.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Actions row */}
        <div className="flex flex-wrap items-center gap-3 mt-6">
          <button data-testid="new-scan-btn" onClick={() => nav("/upload")}
            className="pill-btn cta-metal inline-flex items-center gap-2 text-white font-bold px-4 py-2 text-sm">
            <Plus weight="bold" size={16} aria-hidden />{t("newScan")}
          </button>
          {isPro ? (
            <>
              <button data-testid="export-csv-btn" onClick={exportCsv} className="pill-btn inline-flex items-center gap-2 border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)] font-bold px-4 py-2 text-sm">
                <FileCsv size={16} aria-hidden />{t("exportCsv")}
              </button>
              <button data-testid="export-pdf-btn" onClick={exportPdf} className="pill-btn inline-flex items-center gap-2 border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)] font-bold px-4 py-2 text-sm">
                <FilePdf size={16} aria-hidden />{t("exportPdf")}
              </button>
            </>
          ) : (
            <button data-testid="export-locked-btn" onClick={() => nav("/pro")}
              className="pill-btn inline-flex items-center gap-2 border border-amber-200 bg-amber-50 text-[var(--frumy-lock)] font-bold px-4 py-2 text-sm">
              <Lock weight="fill" size={15} aria-hidden />{t("export")}
            </button>
          )}
          <button data-testid="delete-all-btn" onClick={deleteAll}
            className="pill-btn inline-flex items-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 font-bold px-4 py-2 text-sm ml-auto">
            <Trash size={16} aria-hidden />{t("deleteAll")}
          </button>
        </div>

        {/* Free banner */}
        {!isPro && (hiddenSubs > 0 || hiddenHabs > 0) && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="pro-banner"
            className="mt-6 flex items-center gap-3 rounded-xl p-4 bg-amber-50 border border-amber-200">
            <Crown weight="fill" size={22} className="text-[var(--frumy-lock)]" aria-hidden />
            <p className="text-sm font-semibold text-amber-900 flex-1">{t("proBanner")}</p>
            <button onClick={() => nav("/pro")} className="pill-btn bg-[var(--frumy-lock)] text-white font-bold px-4 py-2 text-sm whitespace-nowrap">{t("goPro")}</button>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-2 gap-6 mt-8">
          {/* Subscriptions */}
          <section>
            <h2 className="flex items-center gap-2 font-display text-2xl font-bold mb-4">
              <ArrowsClockwise size={22} className="text-[var(--frumy-green)]" aria-hidden />{t("subscriptions")}
              <span className="text-sm font-bold text-[var(--text-muted)]">{subs.length}</span>
            </h2>
            <div className="space-y-3">
              {visibleSubs.map((s, i) => {
                const d = daysUntil(s.next_renewal);
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * .04 }}
                    data-testid={`dash-sub-${i}`} className="frumy-card p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-bold text-[var(--heading)]">{s.custom_name}</p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full bg-[var(--frumy-muted)] text-[var(--heading)] border border-[var(--frumy-border)]">{catLabel(s.category)}</span>
                          {isCombined && s.account_name && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-[var(--text-muted)]"><Bank size={11} aria-hidden />{s.account_name}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-display text-lg font-extrabold">{eur(s.amount)}</p>
                        <p className="text-xs text-[var(--text-muted)]">{s.frequency === "monthly" ? t("monthly") : t("annual")}</p>
                      </div>
                    </div>
                    {isCombined && s.duplicate && (
                      <p data-testid={`dup-badge-${i}`} className="flex items-start gap-1.5 mt-2 text-xs font-semibold text-[var(--frumy-lock)] bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 leading-snug">
                        <Warning weight="fill" size={14} className="shrink-0 mt-0.5" aria-hidden />
                        {t("possibleDuplicate")}{s.duplicate_accounts?.length ? ` — ${t("alsoOn")} ${s.duplicate_accounts.join(", ")}` : ""}
                      </p>
                    )}
                    {d != null && (
                      <p className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] mt-3">
                        <CalendarCheck size={14} aria-hidden />{t("nextRenewal")}: <span className="font-bold text-[var(--heading)]">{d >= 0 ? t("inDays", { n: d }) : s.next_renewal}</span>
                      </p>
                    )}
                    {s.advice && <AnnualAdvice advice={s.advice} sub={s} testid={`sub-advice-${i}`} onReport={() => setReportFor(s)} />}
                  </motion.div>
                );
              })}
              {hiddenSubs > 0 && (
                <div data-testid="locked-subs" className="frumy-card p-4 flex items-center gap-3 border-amber-200 bg-amber-50/50">
                  <Lock weight="fill" size={18} className="text-[var(--frumy-lock)]" aria-hidden />
                  <p className="text-sm font-semibold text-amber-900 flex-1">+{hiddenSubs} {t("subscriptions").toLowerCase()}</p>
                  <button onClick={() => nav("/pro")} className="text-sm font-bold text-[var(--frumy-lock)] hover:underline">{t("goPro")}</button>
                </div>
              )}
            </div>
          </section>

          {/* Habituals */}
          <section>
            <h2 className="flex items-center gap-2 font-display text-2xl font-bold mb-4">
              <ShoppingBag size={22} className="text-[var(--frumy-lock)]" aria-hidden />{t("habituals")}
              <span className="text-sm font-bold text-[var(--text-muted)]">{habs.length}</span>
            </h2>
            <div className="space-y-3">
              {visibleHabs.map((h, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * .04 }}
                  data-testid={`dash-hab-${i}`} className="frumy-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-bold text-[var(--heading)]">{h.custom_name}</p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full bg-[var(--frumy-muted)] text-[var(--heading)] border border-[var(--frumy-border)]">{catLabel(h.category)}</span>
                        {isCombined && h.account_name && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-[var(--text-muted)]"><Bank size={11} aria-hidden />{h.account_name}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-lg font-extrabold">{eur(h.total_all != null ? h.total_all : h.total_current_month)}</p>
                      <p className="text-xs text-[var(--text-muted)]">{t("totalPeriod")}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-xs">
                    <span className="text-[var(--text-muted)]">{h.tx_count} {t("transactions")}</span>
                    {h.monthly_avg != null && (
                      <span className="text-[var(--text-muted)]">· {eur(h.monthly_avg)}/{t("monthly").toLowerCase()}</span>
                    )}
                    {h.change_pct != null && (
                      <span className={`flex items-center gap-1 font-bold ${h.change_pct > 0 ? "text-red-500" : "text-[var(--frumy-green)]"}`}>
                        {h.change_pct > 0 ? <TrendUp size={13} aria-hidden /> : <TrendDown size={13} aria-hidden />}
                        {Math.abs(h.change_pct)}% {t("vsPrev")}
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
              {hiddenHabs > 0 && (
                <div data-testid="locked-habs" className="frumy-card p-4 flex items-center gap-3 border-amber-200 bg-amber-50/50">
                  <Lock weight="fill" size={18} className="text-[var(--frumy-lock)]" aria-hidden />
                  <p className="text-sm font-semibold text-amber-900 flex-1">+{hiddenHabs} {t("habituals").toLowerCase()}</p>
                  <button onClick={() => nav("/pro")} className="text-sm font-bold text-[var(--frumy-lock)] hover:underline">{t("goPro")}</button>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Pro sections: trend + history */}
        <div className="grid lg:grid-cols-2 gap-6 mt-10">
          <div>
            <h2 className="flex items-center gap-2 font-display text-xl font-bold mb-3">
              <TrendUp size={20} className="text-[var(--frumy-green)]" aria-hidden />{t("trend")}
            </h2>
            <ProGate isPro={isPro} label={t("proLocked")}>
              <div className="p-5" style={{ height: 240 }} data-testid="trend-chart">
                <ResponsiveContainer>
                  <LineChart data={trendData.length ? trendData : [{ name: "-", total: 0 }]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="name" fontSize={12} />
                    <YAxis fontSize={12} />
                    <Tooltip formatter={(v) => eur(v)} />
                    <Line type="monotone" dataKey="total" stroke="#059669" strokeWidth={3} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </ProGate>
          </div>
          <div>
            <h2 className="flex items-center gap-2 font-display text-xl font-bold mb-3">
              <Bank size={20} className="text-[var(--frumy-green)]" aria-hidden />{t("myAccounts")}
            </h2>
            <ProGate isPro={isPro} label={t("proLocked")}>
              <div className="p-5 space-y-2" data-testid="accounts-list">
                {accounts.map((s, i) => (
                  <button key={i} onClick={() => setSelected(s.id)}
                    className={`w-full flex items-center justify-between py-2 px-2 rounded-lg text-left transition-colors hover:bg-[var(--frumy-muted)] ${!isCombined && selected === s.id ? "bg-[var(--frumy-muted)]" : ""}`}>
                    <span className="flex items-center gap-2 text-sm font-semibold text-[var(--heading)]"><Bank size={15} className="text-[var(--text-muted)]" aria-hidden />{s.account_name}</span>
                    <span className="font-bold text-[var(--heading)]">{eur(s.summary?.monthly_total)}/{t("monthly").toLowerCase()}</span>
                  </button>
                ))}
                {accounts.length === 0 && <p className="text-sm text-[var(--text-muted)]">—</p>}
              </div>
            </ProGate>
          </div>
        </div>

        <div id="frumy-feedback">
          <RatingCard />
          {showFeedback && (
            <FeedbackForm type="parsing" onClose={() => setShowFeedback(false)} onSent={() => setShowFeedback(false)} />
          )}
        </div>
      </div>
      <Footer onFeedback={() => { setShowFeedback(true); setTimeout(() => document.getElementById("frumy-feedback")?.scrollIntoView({ behavior: "smooth" }), 50); }} />
      {reportFor && <ReportDialog sub={reportFor} onClose={() => setReportFor(null)} />}
    </div>
  );
}
